import { useCallback, useEffect, useState } from "react";
import { C } from "../lib/theme.js";
import { api } from "../lib/api.js";

// Upload form + history table. One file may contain N property sheets; the
// server now handles that in a single POST /api/ingest and returns
// `{ uploaded: [...], skipped: [...] }`.
export default function ManageUploads({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const rows = await api.listUploads();
      setHistory(rows || []);
    } catch (e) {
      setError(e.message);
    }
    setLoadingHistory(false);
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.uploadSnapshot({ file });
      setResult(res);
      setFile(null);
      // Clear the file input so picking the same file again fires `change`.
      e.target.reset?.();
      await loadHistory();
      if (onUploaded) await onUploaded(res);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  return (
    <div>
      <div style={sectionHead}>Upload a Budget Variance workbook</div>
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 10 }}>
        One .xlsx, one sheet per property. Property code comes from the sheet name ("060", "125", etc.);
        period comes from cell A4 ("March 2026 - Accrual...").
      </div>

      <form onSubmit={submit} style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 120px" }}>
        <input
          type="file"
          accept=".xlsx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={fieldStyle}
        />
        <button type="submit" disabled={busy || !file} style={{ ...btn, opacity: busy || !file ? 0.5 : 1 }}>
          {busy ? "Uploading…" : "Upload"}
        </button>
      </form>

      {error && (
        <div style={{ ...alertStyle, borderColor: C.red + "40", background: C.redBg, color: C.red }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{ ...alertStyle, borderColor: C.accent + "40", background: C.accentLight, color: C.text }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, marginBottom: 4 }}>
            Uploaded {result.uploaded?.length || 0} propert{(result.uploaded?.length || 0) === 1 ? "y" : "ies"}
            {result.primary_period ? ` for ${formatPeriod(result.primary_period)}` : ""}
          </div>
          {result.uploaded?.length > 0 && (
            <div style={{ fontSize: 11, color: C.sub }}>
              {result.uploaded.map((u) => u.code).join(" · ")}
            </div>
          )}
          {result.skipped?.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 11, color: C.amber }}>
              Skipped {result.skipped.length}: {result.skipped.map((s) => `${s.sheet} (${s.reason})`).join("; ")}
            </div>
          )}
        </div>
      )}

      <div style={{ ...sectionHead, marginTop: 24 }}>Upload history</div>
      {loadingHistory ? (
        <div style={{ color: C.sub, fontSize: 12 }}>Loading…</div>
      ) : history.length === 0 ? (
        <div style={{ color: C.dim, fontSize: 12, fontStyle: "italic" }}>No uploads yet.</div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden" }}>
          <div style={{ ...histHeaderRow }}>
            <span>Period</span>
            <span>Property</span>
            <span>Uploaded</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Source file</span>
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {history.map((r, i) => (
              <div key={`${r.property_code}:${r.period}:${i}`} style={histRow}>
                <span style={{ fontFamily: "'DM Mono',monospace", color: C.sub }}>{formatPeriod(r.period)}</span>
                <span>
                  <span style={{ fontWeight: 500 }}>{r.alias || r.property_code}</span>{" "}
                  <span style={{ color: C.dim, fontFamily: "'DM Mono',monospace", fontSize: 11 }}>({r.property_code})</span>
                </span>
                <span style={{ fontFamily: "'DM Mono',monospace", color: C.dim, fontSize: 11 }}>
                  {formatUploadedAt(r.uploaded_at)}
                </span>
                {r.source_path ? (
                  <a
                    href={api.uploadFileUrl(r.source_path)}
                    title={`Download ${basename(r.source_path)}`}
                    download
                    style={{
                      color: C.accent,
                      textDecoration: "none",
                      fontSize: 11,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {basename(r.source_path)}
                  </a>
                ) : (
                  <span
                    style={{ color: C.dim, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    —
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function basename(p) {
  if (!p) return "";
  const parts = p.split(/[\\/]/);
  return parts[parts.length - 1];
}

function formatPeriod(p) {
  if (!p) return "";
  const [y, m] = p.split("-");
  if (!y || !m) return p;
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-US", { month: "short", year: "numeric" });
}

function formatUploadedAt(s) {
  if (!s) return "";
  // SQLite datetime('now') is UTC with "YYYY-MM-DD HH:MM:SS"; add Z so the
  // browser parses it as UTC then renders in local time.
  const d = new Date(s.includes("T") ? s : s.replace(" ", "T") + "Z");
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const sectionHead = {
  fontSize: 13,
  fontWeight: 600,
  color: C.text,
  marginBottom: 6,
};

const fieldStyle = {
  padding: "6px 8px",
  border: `1px solid ${C.border}`,
  borderRadius: 5,
  fontSize: 12,
  fontFamily: "'Inter',sans-serif",
};

const btn = {
  background: C.accent,
  color: "#fff",
  border: "none",
  borderRadius: 5,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  padding: "6px 14px",
};

const alertStyle = {
  marginTop: 12,
  padding: "8px 12px",
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  fontSize: 12,
};

const histHeaderRow = {
  display: "grid",
  gridTemplateColumns: "110px 1fr 140px 1.4fr",
  gap: 8,
  padding: "8px 12px",
  background: C.bg,
  borderBottom: `1px solid ${C.border}`,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: C.dim,
};

const histRow = {
  display: "grid",
  gridTemplateColumns: "110px 1fr 140px 1.4fr",
  gap: 8,
  padding: "8px 12px",
  borderBottom: `1px solid ${C.borderLight}`,
  alignItems: "center",
  fontSize: 12,
};
